import React, { CSSProperties, HTMLAttributes, ReactNode, useMemo } from 'react';
import {
    AnimateLayoutChanges,
    UseSortableArguments,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { getIsOverParent, iOS } from './utilities';
import type { FlattenedItem, TreeItem, TreeItemComponentType } from './types';
import { UniqueIdentifier } from '@dnd-kit/core';
import { AtLeast } from 'common/types/Atleast';

export interface TreeItemProps<T> extends HTMLAttributes<HTMLLIElement> {
    childCount?: number;
    clone?: boolean;
    isOpen?: boolean;
    depth: number;
    disableInteraction?: boolean;
    disableSelection?: boolean;
    ghost?: boolean;
    handleProps?: any;
    indicator?: boolean;
    indentationWidth: number;
    item: TreeItem<T>;
    isLast: boolean;
    parentsKeys: string[];
    parent: FlattenedItem<T> | null;
    children?: ReactNode;
    onCollapse?: (id: UniqueIdentifier) => void;

    onRemove?: (id: UniqueIdentifier) => void;

    wrapperRef?: (node: HTMLLIElement) => void;
}

const animateLayoutChanges: AnimateLayoutChanges = ({
    isSorting,
    isDragging,
}) => (!(isSorting || isDragging));

type SortableTreeItemProps<
    T,
    TElement extends HTMLElement
> = TreeItemProps<T> & {
    id: string;
    index: number;
    TreeItemComponent: TreeItemComponentType<T, TElement>;
    disableSorting?: boolean;
    sortableProps?: Omit<UseSortableArguments, 'id'>;
    keepGhostInPlace?: boolean;
    onItemChange?: (item: AtLeast<FlattenedItem<T>, 'key'>, type: 'change' | 'remove' | 'change-self-and-children') => void;
    onItemCreate?: (item: T) => void;
    onSelectItem?: (key: string) => void;
    onMoveItem?: (key: string,type: 'up' | 'down') => void;
};

const SortableTreeItemNotMemoized = function SortableTreeItem<
    T,
    TElement extends HTMLElement
>({
    id,
    depth,
    isLast,
    parentsKeys,
    TreeItemComponent,
    parent,
    disableSorting,
    sortableProps,
    keepGhostInPlace,
    onItemChange,
    onItemCreate,
    onSelectItem,
    onMoveItem,
    index,
    ...props
}: SortableTreeItemProps<T, TElement>) {
    const {
        attributes,
        isDragging,
        isSorting,
        listeners,
        setDraggableNodeRef,
        setDroppableNodeRef,
        transform,
        transition,
        isOver,
        over,
    } = useSortable({
        id,
        animateLayoutChanges,
        disabled: disableSorting,
        ...sortableProps,
    });
    const isOverParent = useMemo(
        () => !!over?.id && getIsOverParent(parent, over.id),
        [over?.id]
    );
    const style: CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition: transition ?? undefined,
    };
    const localCollapse = useMemo(() => {
        if (!props.onCollapse) return undefined;
        return () => props.onCollapse?.(props.item.key);
    }, [props.item.key, props.onCollapse]);

    const localRemove = useMemo(() => {
        if (!props.onRemove) return undefined;

        return () => props.onRemove?.(props.item.key);
    }, [props.item.key, props.onRemove]);
    return (
        <TreeItemComponent
            {...props}
            ref={setDraggableNodeRef}
            wrapperRef={setDroppableNodeRef}
            style={keepGhostInPlace ? undefined : style}
            depth={depth}
            ghost={isDragging}
            disableSelection={iOS}
            disableInteraction={isSorting}
            isLast={isLast}
            parent={parent}
            parentsKeys={parentsKeys}
            handleProps={{
                ...attributes,
                ...listeners,
            }}
            onCollapse={localCollapse}
            onRemove={localRemove}
            disableSorting={disableSorting}
            disabled={disableSorting ?? false}
            isOver={isOver}
            isOverParent={isOverParent}
            onItemChange={onItemChange}
            onItemCreate={onItemCreate}
            onSelectItem={onSelectItem}
            onMoveItem={onMoveItem}
            index={index}
        />
    );
};

// `flattenTree` rebuilds wrapper objects on every SortableTree render, so the default
// shallow compare (ref on `item`) always invalidates — even when the underlying line
// didn't change. Compare the wrapper's fields instead: unchanged lines keep the same
// field references in Redux so most renders short-circuit here.
function shallowEqualItem(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const k of keysA) {
        if (a[k] !== b[k]) return false;
    }
    return true;
}

function arePropsEqual<T, TElement extends HTMLElement>(
    prev: SortableTreeItemProps<T, TElement>,
    next: SortableTreeItemProps<T, TElement>
): boolean {
    if (prev.id !== next.id) return false;
    if (prev.depth !== next.depth) return false;
    if (prev.index !== next.index) return false;
    if (prev.isLast !== next.isLast) return false;
    if (prev.childCount !== next.childCount) return false;
    if (prev.isOpen !== next.isOpen) return false;
    if (prev.indentationWidth !== next.indentationWidth) return false;
    if (prev.disableSorting !== next.disableSorting) return false;
    if (prev.indicator !== next.indicator) return false;
    if (prev.keepGhostInPlace !== next.keepGhostInPlace) return false;
    if (prev.TreeItemComponent !== next.TreeItemComponent) return false;
    if (prev.onItemChange !== next.onItemChange) return false;
    if (prev.onItemCreate !== next.onItemCreate) return false;
    if (prev.onSelectItem !== next.onSelectItem) return false;
    if (prev.onMoveItem !== next.onMoveItem) return false;
    if (prev.onCollapse !== next.onCollapse) return false;
    if (prev.onRemove !== next.onRemove) return false;

    // parentsKeys array — compare length + contents
    const prevKeys = prev.parentsKeys;
    const nextKeys = next.parentsKeys;
    if (prevKeys !== nextKeys) {
        if (!prevKeys || !nextKeys) return false;
        if (prevKeys.length !== nextKeys.length) return false;
        for (let i = 0; i < prevKeys.length; i++) {
            if (prevKeys[i] !== nextKeys[i]) return false;
        }
    }

    // parent wrapper — compare by key only (ref always differs)
    if ((prev.parent?.key ?? null) !== (next.parent?.key ?? null)) return false;

    // item wrapper is rebuilt every render; compare its shallow fields
    if (!shallowEqualItem(prev.item, next.item)) return false;

    return true;
}

export const SortableTreeItem = React.memo(
    SortableTreeItemNotMemoized,
    arePropsEqual
) as typeof SortableTreeItemNotMemoized;
